<template>
    <div class="h-full w-full ">
        <div  v-if="finishedAction" class="w-full h-full p-4 text-gray-700 tracking-wider font-medium">
            <div v-if="responseStatus == 200 || responseStatus == 201" class="w-full h-full justify-between">
                <div class="flex flex-col justify-start items-center space-y-3 p-3 h-[90%]">
                    <div v-if="bulkAction.requireSelectedResults" class="flex flex-col justify-start items-center">
                        This action has been performed on {{ numOperatedResults }} results. 
                    </div>
                    <div v-else  class="flex flex-col justify-start items-center">
                        This action has been performed on all results.
                    </div>
                    <div class="w-full flex items-start justify-start">
                        Closing this response in {{ timeRemaining }} seconds.
                    </div>
                </div>
                <div class="h-10 flex items-center justify-center">
                    <BaseButtonCancel
                        @click="cancelBulkOperation"
                    >
                        Close
                    </BaseButtonCancel>
                </div>
            </div>
            <div  v-else>
                <p>{{ title }}: Failed - Bad Request.</p>
                <br>
                <p>An error occurred that prevented the completion of this action.</p>
            </div>
        </div>
        <div v-else class="w-full h-full">
            <div v-if="isLoading" class="p-4 w-full h-full flex justify-center items-center">
                <svg aria-hidden="true" class="mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600"
                        viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="white"/>
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="#2E9CCA"/>
                </svg>
                <span class="sr-only">Loading...</span>
            </div>
            <div v-else class="w-full h-full">
                <div class="w-full h-full">
                    <form action="" @submit.prevent="" class="flex flex-col h-full w-full justify-between">
                        <div v-if="visibleSelectedResults" class="h-[calc(100%-3.5rem)] w-full overflow-y-scroll border-b-2 border-gray-300">
                            <SelectedResultCard
                                v-for="(result, index) in selectedResults"
                                :key="result.id" 
                                :index="index"
                                :result="result"
                                :displayedFields="selectedResultFields"
                            />
                        </div>
                        <div v-else class="h-[calc(100%-3.5rem)] overflow-y-scroll border-b-2 border-gray-300">
                            <div v-for="(inputValue, inputKey) in bulkAction.inputs" :key="inputKey" class="p-2 flex flex-col space-y-1">
                                <div v-if="inputValue.visible">
                                    <div class="tracking-wider font-medium px-1 pb-1 text-gray-800">{{ inputValue.label }}<span v-if="inputValue.required">*</span>:</div>
                                    <div>
                                        <ComboBox v-if="inputValue.widget == 'combo-box' && inputValue.multiSelect"
                                            :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :required="inputValue.required"
                                            :options="inputValue.options"
                                            :multiple="inputValue.multiSelect"
                                            v-model="inputValue.value"
                                            :placeholder="inputValue.placeholder"
                                        />
                                        <ComboBox v-else-if="inputValue.widget == 'combo-box'"
                                            v-model="inputValue.value"
                                            :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :required="inputValue.required"
                                            :options="inputValue.options"
                                            :placeholder="inputValue.placeholder"
                                            :multiple="inputValue.multiSelect"
                                        />
                                        <ListBox v-else-if="inputValue.widget == 'list-box' && inputValue.multiSelect"
                                            :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :required="inputValue.required"
                                            :options="inputValue.options"
                                            :placeholder="inputValue.placeholder"
                                            :multiple="inputValue.multiSelect"
                                            @update:modelValue="(event) => handleListBoxMultiSelectChange(`${inputKey}`, event)"
                                        />
                                        <ListBox v-else-if="inputValue.widget == 'list-box'"
                                            v-model="inputValue.value"
                                            :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :required="inputValue.required"
                                            :options="inputValue.options"
                                            :multiple="inputValue.multiSelect"
                                            @update:model-value="(event) => handleListBoxChange(`${inputKey}`, event)"
                                        />
                                        <div v-else-if="inputValue.widget == 'datalist'" class="">
                                            <input
                                                type="text"
                                                :id="`input-${index}`"
                                                :list="`datalist-${index}`"
                                                :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                                :required="inputValue.required"
                                                @change="setDataListInput(`${inputKey}`, $event)"
                                            />
                                            <datalist :id="`datalist-${index}`">
                                                <option value="null">------------</option>
                                                <option v-for="option in inputValue.options" :key="option.value" :value="`${option.value}`">{{ option.label }}</option>
                                            </datalist>
                                        </div>
                                        <input v-else-if="inputValue.widget == 'input'"
                                            :class="'bg-white px-2 h-8 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :required="inputValue.required"
                                            v-model="inputValue.value"
                                            :type="inputValue.type"
                                            :placeholder="inputValue.placeholder"
                                        />
                                        <textarea v-else-if="inputValue.widget == 'textarea'"
                                            :class="'bg-white px-2 border rounded-sm w-full border-gray-300 text-gray-800 block  focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500'"
                                            :rows="inputValue.rows"
                                            :required="inputValue.required"
                                            v-model="inputValue.value"
                                            :type="inputValue.type"
                                            :placeholder="inputValue.placeholder"
                                        ></textarea>
                                        <div v-else-if="inputValue.widget == 'checkbox'" class="w-full flex justify-end items-center -mt-[20px] pr-1">
                                            <input 
                                                class="accent-dark-secondary cursor-pointer"
                                                :required="inputValue.required"
                                                :checked="inputValue.value"
                                                v-model="inputValue.value"
                                                type="checkbox"
                                            />
                                        </div>
                                    </div>
                                    <div v-if="Array.isArray(inputValue.value) && inputValue.value.length > 0" class="flex flex-col space-y-4">
                                        <div v-if="inputValue.showSelectedInputValues" class="flex flex-col space-y-1">
                                            <div class="tracking-wider font-medium px-1 pt-2 border-t border-gray-300">Chosen Options:</div>
                                            <div class="flex flex-wrap">
                                                <div 
                                                    v-for="value in inputValue.value" 
                                                    :key="value"
                                                    @mouseenter="handleInputEnter(value)"
                                                    @mouseleave="handleInputLeave(value)"
                                                    @click="removeInputResult(inputKey, value)" 
                                                    class="hover:cursor-pointer m-1 rounded-full bg-dark-secondary hover:bg-dark-primary text-white font-medium tracking-wide px-2 py-1 flex items-center"
                                                >
                                                    {{ value }}
                                                <span v-if="selectedInputValueHover==value" class="ml-1">
                                                    <XMarkSVG stroke="white" width="2" iconClass="w-3 h-3"/>
                                                </span>
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- <div v-else class="flex flex-col space-y-4">
                                        <select :id="`input-${index}`"
                                            class="bg-white p-1 border rounded-sm w-full border-gray-300 text-gray-800 block"
                                            @input="setMultiSelectInput($event, 'string')"
                                            >
                                            <option value="null">------------</option>
                                            <option v-for="option in inputValue.options" :key="option.value" :value="`${option.value}`">{{ option.label }}</option>
                                        </select>
                                    </div> -->
                                </div>
                            </div>
                            <div v-if="bulkAction.requireSelectedResults" class="flex flex-col space-y-2 px-4 py-2 ">
                                <div class="flex justify-center items-center">
                                    <input type="checkbox" id="checkbox" class="align-middle accent-dark-secondary cursor-pointer" :checked="keepSelectedResults" @change="toggleKeepSelectedResults"/><span class="pl-2 align-middle text-end">Keep Current Selection</span>
                                </div>
                                <div class="">
                                    <p class="text-center text-indigo-500 hover:text-dark-primary cursor-pointer" @click="toggleShowSelectedResults"><span v-if="visibleSelectedResults">Hide</span><span v-else>Show</span> {{ numSelectedResults }} Selected Results</p>
                                </div>
                            </div>
                            <!-- <div>
                                {{ Object.values(bulkAction.inputs).map((input) => input.value) }}
                            </div>
                            <div>{{ bulkAction.inputs }}</div> -->
                        </div>
                        <div class="space-x-3 h-12 flex items-center justify-center fixed bottom-0 z-10" :class="width">
                            <BaseButton type="button" @click="submitForm">Submit</BaseButton>
                            <BaseButtonCancel type="button" @click="cancelBulkOperation">Cancel</BaseButtonCancel>
                        </div>
                    </form>
                    <br>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, watch, reactive } from 'vue';
import ComboBox from './ComboBox.vue';
import ListBox from './ListBox.vue';
import BaseButton from '../base-components/BaseButton.vue';
import BaseButtonCancel from '../base-components/BaseButtonCancel.vue';
import SelectedResultCard from './SelectedResultCard.vue';
import XMarkSVG from '../svg-components/XMarkSVG.vue';

const props = defineProps([
    'bulkAction',
    'selectedResults',
    'selectedResultFields',
    'viewType',
    'fetchFilter',
    'response',
    'width'
])

const emit = defineEmits([
    'cancel-bulk-operation',
    'refetch-results',
    'clear-selected-results',
    'perform-bulk-action',
    'handle-bulk-operation-input-change'
])

watch(() => props.response, () => {
    handleResponse(props.response)
}, {deep:true})

const isLoading = ref(false);
const finishedAction = ref(false);
const responseStatus = ref(null);
const bulkAction = reactive(props.bulkAction)
const selectedInputValueHover = ref(null);
const keepSelectedResults = ref(false);
const visibleSelectedResults = ref(false);
const numOperatedResults = ref(0)
const timeRemaining = ref(10)

const title = computed(() => {
    if(props.bulkAction) {
        if(props.bulkAction.label) {
            return props.bulkAction.label
        }
    }
    return ''
});

const numSelectedResults = computed(() => {
    if(props.selectedResults){
        if(props.selectedResults.length > 0) {
            return props.selectedResults.length
        }
    }
    return 0
})

const bulkOperationQueryParams = computed(() => {
    if(props.selectedResults.length == 0) {
        return props.fetchFilter
    }
    return  null
})

function handleListBoxChange(inputKey, event) {
    if(bulkAction.inputs[inputKey].onUpdate) {
        emit('handle-bulk-operation-input-change', bulkAction, inputKey, event)
    }
}

function handleListBoxMultiSelectChange(inputKey, event) {
    console.log('input key', inputKey)
    console.log('event', event)
    if(event[0]){
        if(!bulkAction.inputs[inputKey].value.includes(event[0])) {
         bulkAction.inputs[inputKey].value.push(event[0])
            if(bulkAction.inputs[inputKey].onUpdate) {
                emit('handle-bulk-operation-input-change', bulkAction, inputKey, event)
            }
        }
    }
}

function setDataListInput(inputKey, event) {
    if(event.target.value){
        if(Array.isArray(bulkAction.inputs[inputKey].value)){
            if(!bulkAction.inputs[inputKey].value.includes(event.target.value)){
                bulkAction.inputs[inputKey].value.push(event.target.value)
            }
        } else {
            bulkAction.inputs[inputKey].value = [event.target.value]
        }
        event.target.value = null
    }
}

function removeInputResult(inputKey, inputValue){
    bulkAction.inputs[inputKey].value.splice(bulkAction.inputs[inputKey].value.indexOf(inputValue), 1)
}

function cancelBulkOperation() {
    emit('cancel-bulk-operation')
}

function handleInputEnter(inputValue) {
    selectedInputValueHover.value = inputValue
}

function handleInputLeave(inputValue) {
    selectedInputValueHover.value = null
}

function toggleShowSelectedResults() {
    visibleSelectedResults.value = !visibleSelectedResults.value
}

function toggleKeepSelectedResults() {
    keepSelectedResults.value = !keepSelectedResults.value
}

function handleResponse(response) {
    // console.log('handleResponse', response)
    finishedAction.value = true;
    responseStatus.value = response.status
    isLoading.value = false;
    let timer = 10
    const countdownInterval = setInterval(() => {
        // console.log("setInterval")
        let seconds = timer % 60;
        seconds = seconds < 10 ? '0' + seconds : seconds;
        timeRemaining.value = seconds
        if(--timer < 0) {
            cancelBulkOperation()
            clearInterval(countdownInterval)
        }
    }, 1000)
    // if(responseStatus.value == 200) {
    //     $toast.open({
    //         message:`Completed bulk operation: ${bulkAction.label}`,
    //         type: 'success',
    //         duration: 3000
    //     })
    //     emit('refetch-results')
    //     if(!keepSelectedResults.value) {
    //         emit('clear-selected-results')
    //     }
    // } else {
    //     $toast.open({
    //         message: `Could not complete bulk operation: ${bulkAction.label}`,
    //         type: 'error',
    //         duration: 10000
    //     })
    // }
}

function validatePayload(actionPayloadType) {
    // console.log('validatePayload', actionPayloadType)
    console.log(bulkAction.inputs)
    let payload = null
    let invalidNullInputs = []
    if(actionPayloadType == 'simple') {
        payload = Object.values(bulkAction.inputs)[0].value
    } else if (actionPayloadType == 'form') {
        payload = {}
        Object.values(bulkAction.inputs).filter((input) => !input.excludeFromPayload).forEach((input) => {
            if(input.required && !input.value){ 
                invalidNullInputs.push((input.label))
            } else {
                payload[input.key] = input.value
            }
        })
        // console.log(payload)
        // return payload
    }
    if(invalidNullInputs.length > 0) {
        let errorMessage = 'Error: The following fields are null and require values: '
        console.log(invalidNullInputs)
        invalidNullInputs.forEach((inputLabel) => {
            errorMessage += `${inputLabel}, `
        })
        alert(errorMessage)
        payload = 'error'
    }
    return payload
}

function submitForm() {
    // console.log('bulkOp: submitForm')
    const payload = validatePayload(bulkAction.payloadType)
    if(payload != 'error') {
        isLoading.value = true
        let bulkActionData
        if(bulkAction.requireSelectedResults) {
            // const selectedResultIds = props.selectedResults.map(({id}) => id)
            numOperatedResults.value = props.selectedResults.length
            if(bulkAction.limit && numOperatedResults.value > bulkAction.limit) {
                alert(`Alert:  This bulk operation has a limit of ${bulkAction.limit} records. Please reduce the number of selected results (${numOperatedResults.value} total) to proceed.`)
                isLoading.value = false
                return
            } else {
                bulkActionData = {
                    key: bulkAction.key,
                    type: 'onSelected',
                    label: bulkAction.label,
                    overrideAction: bulkAction.overrideAction,
                    selectedResults: props.selectedResults,
                    endpoint: bulkAction.endpoint,
                    payload: payload,
                    keepSelected: keepSelectedResults.value,
                }
            }

        } else if(bulkOperationQueryParams.value) {
            bulkActionData = {
                key: bulkAction.key,
                type: 'onQuery',
                label: bulkAction.label,
                overrideAction: bulkAction.overrideAction,
                queryParams: bulkOperationQueryParams.value,
                endpoint: bulkAction.endpoint,
                payload: payload,
                keepSelected: keepSelectedResults.value
            }
        }
        // console.log('bulkActionData')
        // console.log(bulkActionData)
        emit('perform-bulk-action', bulkActionData)
    }
    // handleResponse(response.value)
}
</script>